import React from "react"
import { zodResolver } from "@hookform/resolvers/zod"
import { DropdownMenuSeparator } from "@radix-ui/react-dropdown-menu"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useParams } from "@tanstack/react-router"
import { useForm } from "react-hook-form"
import { z } from "zod"

import { MeterSizeRateType, MeterSizeType } from "@/types/meter"
import { ConnectionType } from "@/types/zone-connection-rates-type"
import {
  addMeterSizeRate,
  editMeterSizeRate,
  getConnectionType,
  getMeterSizes,
} from "@/lib/api/meter"
import { useToast } from "@/hooks/use-toast"

import { ComboboxSearch } from "../combobox-search"
import { Header } from "../header"
import { Icons } from "../icons"
import { Button } from "../ui/button"
import { Card } from "../ui/card"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form"
import { Input } from "../ui/input"

interface MeterSizeRateFormProps {
  onClose?: () => void
  setShowModal?: (showModal: boolean) => void
  editData?: MeterSizeRateType
}

const schema = z.object({
  rate_type_id: z.coerce.number().min(1, "Please select a connection type"),
  meter_size_id: z.coerce.number().min(1, "Please select a meter size"),
  min_volume: z.coerce.number().min(0, "Minimum volume is required"),
  max_volume: z.coerce.number().nullable(),
  minimum_charge: z.coerce.number().min(0, "Minimum charge is required"),
})

export default function MeterSizeRateForm({
  onClose,
  setShowModal,
  editData,
}: MeterSizeRateFormProps) {
  const { toast } = useToast()
  const { id } = useParams({ strict: false })
  const queryClient = useQueryClient()

  const { data: connectionTypeData } = useQuery({
    queryKey: ["connection_type"],
    queryFn: getConnectionType,
  })

  const {
    data: meterSizeData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["metersize"],
    queryFn: () => getMeterSizes(),
  })

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      rate_type_id: editData?.rate_type_id ?? 0,
      meter_size_id: editData?.meter_size_id ?? 0,
      min_volume: editData?.min_volume ?? 1,
      max_volume: editData?.max_volume ?? 1,
      minimum_charge: Number(editData?.minimum_charge) ?? 1,
    },
  })

  const mutate = useMutation({
    mutationFn: async (data: any) => {
      if (editData) {
        return await editMeterSizeRate(editData.id, data)
      }
      return await addMeterSizeRate(data)
    },
    onSuccess: () => {
      editData
        ? toast({
            description: "Meter size rate updated successfully",
          })
        : toast({
            description: "Meter size rate added successfully",
          })
          
      queryClient.invalidateQueries({ queryKey: ["metersizerates"] })

      setShowModal?.(false)
      onClose?.()
    },
    onError: (error: any) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      })
      form.setError("rate_type_id", { message: "" })
      form.setError("meter_size_id", {
        message: "Connection type is already in use",
      })
    },
  })

  const connectionTypes: Array<{
    value: string
    label: string
    id?: string | number
  }> =
    (connectionTypeData as ConnectionType[]) // Ensure data is an array
      ?.map((item) => ({
        value: item.type_name,
        label: item.type_name,
        id: item.id,
      }))
      .filter(
        (item, index, self) =>
          index === self.findIndex((t) => t.value === item.value), // Ensure unique values
      ) ?? []

  const meterSizes: Array<{
    value: string
    label: string
    id?: string | number
  }> =
    (meterSizeData as MeterSizeType[]) // Ensure data is an array
      ?.map((item) => ({
        value: item.size,
        label: item.size,
        id: item.id,
      }))
      .filter(
        (item, index, self) =>
          index === self.findIndex((t) => t.value === item.value), // Ensure unique values
      ) ?? []

  const onSubmit = async (data: z.infer<typeof schema>) => {
    mutate.mutate(data)
  }

  const handleError = (errors: any) => {
    console.error("Form validation errors:", errors)

    // Optional: show toast
    toast({
      title: "Form validation failed",
      description: "Please check required fields.",
      variant: "destructive",
    })
  }

  return (
    <div className="px-2">
      <Header
        subroutes={[
          {
            route: "/meter-connection-settings/meter-size-rates",
            label: "Meter Size Rates",
          },
        ]}
      >
        {`${editData ? "Edit" : "Add"} Meter Size Rate`}
      </Header>

      <DropdownMenuSeparator className="mt-4 h-[1px] bg-muted" />

      <Form {...form}>
        <form
          className="grid grid-cols-1 gap-4 lg:container md:max-w-[1000px]"
          onSubmit={form.handleSubmit(onSubmit, handleError)}
        >
          <Card className="mb-3 space-y-4 border-0">
            <FormField
              control={form.control}
              name="rate_type_id"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel className="py-1">
                    Connection type
                    <span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <ComboboxSearch
                      field={{
                        ...field,
                      }}
                      options={connectionTypes}
                      placeholder="Select a connection type"
                      idAsValue
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex flex-col space-y-1.5">
              <FormField
                control={form.control}
                name="meter_size_id"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel className="py-1">
                      Meter size
                      <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <ComboboxSearch
                        field={{
                          ...field,
                        }}
                        options={meterSizes}
                        placeholder="Select meter size"
                        idAsValue
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="flex flex-col space-y-1.5">
              <FormField
                control={form.control}
                name="minimum_charge"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel className="py-1">
                      Minimum Charge
                      <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder="Minimum charge"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </Card>

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowModal?.(false)}
            >
              Cancel
            </Button>

            <Button type="submit" disabled={mutate.isPending}>
              {mutate.isPending && (
                <Icons.loader className="mr-2 animate-spin" />
              )}
              {editData ? "Update" : "Add"} Meter Size Rate
            </Button>
          </div>
        </form>
      </Form>
    </div>
  )
}
